/**
 * QuizListSkeleton Component
 * Loading skeleton for quiz list items
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const SkeletonContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SkeletonLine = styled.div`
  height: ${props => props.height || '1rem'};
  background: linear-gradient(
    90deg,
    var(--background-secondary) 0%,
    var(--background-tertiary) 50%,
    var(--background-secondary) 100%
  );
  background-size: 1000px 100%;
  border-radius: var(--border-radius);
  animation: ${shimmer} 2s infinite;
  width: ${props => props.width || '100%'};
`;

const SkeletonChips = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SkeletonMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  min-width: 140px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: auto;
  }
`;

const SkeletonActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

function QuizListSkeleton({ count = 3 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={index}>
          <SkeletonContent>
            <SkeletonLine height="1.5rem" width="60%" />
            <SkeletonLine height="0.875rem" width="90%" />
            <SkeletonLine height="0.875rem" width="70%" />
            <SkeletonChips>
              <SkeletonLine height="1.75rem" width="100px" />
              <SkeletonLine height="1.75rem" width="120px" />
            </SkeletonChips>
          </SkeletonContent>
          <SkeletonMeta>
            <SkeletonLine height="0.875rem" width="100px" />
            <SkeletonLine height="0.875rem" width="110px" />
          </SkeletonMeta>
          <SkeletonActions>
            <SkeletonLine height="2.5rem" width="80px" />
            <SkeletonLine height="2rem" width="2rem" />
          </SkeletonActions>
        </SkeletonCard>
      ))}
    </>
  );
}

export default QuizListSkeleton;
